<template>
  <div
    class="d-flex justify-content-between align-items-center"
    tabindex="0"
    @focusout="onBlur($event)"
  >
    <div class="title-container" translate="no">
      <div
        :class="`h4 title-edit ${editable ? 'editable' : ''}`"
        @click.prevent="edit($event)"
        :contenteditable="editable"
        :autofocus="!!autofocus"
        spellcheck="false"
        @keypress.enter.prevent="$emit('approve')"
        @keyup="update($event.target.innerText)"
        ref="title_edit"
      ></div>
      <div class="title-display h4">
        <b-skeleton
          v-if="!page.url"
          width="80%"
          height="30px"
          style="margin-top: 3px"
        />
        <span
          v-else
          v-for="(fragment, i) of title.fragments"
          :key="i"
          :class="`fragment ${fragment.labels.join(' ')}`"
        >
          <div
            v-if="fragment.labels.includes('boilerplate')"
            class="boilerplate-marker"
          >
            <span
              @click="removeFragment(fragment.fragment)"
              class="cursor-pointer"
              >&nbsp;Boilerplate<span class="hover">&times;</span></span
            >
          </div>
          <span
            v-for="(word, x) of fragment.words"
            :key="x"
            :class="`word page_title_word ${word.labels.join(' ')}`"
          >
            {{ word.word }}
          </span>
        </span>
      </div>
    </div>
    <b-button
      v-if="actions.length == 1"
      ref="action_button"
      variant="primary"
      @click="actions[0].onClick($event)"
      >{{ actions[0].label }}</b-button
    >
    <b-dropdown
      v-else-if="actions.length > 1"
      split
      :text="actions[0].label"
      ref="action_button"
      variant="primary"
      @click="actions[0].onClick()"
    >
      <b-dropdown-item
        v-for="action of actions.slice(1)"
        :key="action.label"
        @click="action.onClick($event)"
        v-b-tooltip.left="action.tooltip"
        >{{ action.label }}</b-dropdown-item
      >
    </b-dropdown>
  </div>
</template>
<style lang="scss">
.title-container {
  cursor: text;
  position: relative;
  width: 100%;
  margin-right: 10px;

  .h4 {
    margin: 0;
    line-height: 1.5;
  }
}
.title-edit {
  position: relative;
  z-index: 1;
  border: 1px solid transparent;
  outline: 0;
  padding: 3px;
  &.editable,
  &:hover {
    border-color: var(--light-blue);
  }
}
.title-display {
  position: absolute;
  top: 0;
  color: transparent;
  width: 100%;
}
.fragment {
  position: relative;
  &.boilerplate {
    .unranked {
      border: none;
    }
    &:after {
      content: ' ';
      position: absolute;
      display: block;
      width: 100%;
      height: 5px;
      border: 1px solid var(--gray);
      border-top: 0;
      bottom: -8px;
      left: 0;
      margin-top: 5px;
    }
    .boilerplate-marker {
      z-index: 1;
      text-transform: uppercase;
      position: absolute;
      width: 100%;
      left: 0;
      bottom: -14px;
      text-align: center;
      font-size: 0.6rem;
      color: var(--gray);
      > span {
        background: #fff;
      }
    }
  }
}
.hover {
  visibility: hidden;
  *:hover > & {
    visibility: visible;
  }
}
.word {
  &.ranked {
    border-bottom: 3px solid var(--success);
  }
  &.too_far {
    border-bottom: 3px solid #ccc;
  }
}
</style>
<script>
import _ from 'lodash'
import PAGE_QUERY from '@/graphql/PageQuery.gql'
import PAGE_TITLE_DETAILS_QUERY from '@/graphql/PageTitleDetailsQuery.gql'
import PAGE_TITLE_DETAILS_MUTATION from '@/graphql/PageTitleDetailsMutation.gql'
import SITE_FRAGMENTS_QUERY from '@/graphql/SiteFragmentsQuery.gql'

const MIN_COUNT = 5

export default {
  name: 'TitleEditor',
  components: { },
  props: ['account', 'page', 'site', 'autofocus', 'isDemo', 'isDuda'],

  data: () => {
    return {
      loading: false,
      editable: false,
      title: {},
      fragment: [],
      edited: false,
      loaded: false,
      serp_title: {},
      custom_title: null,
      clipboardText: null,
    }
  },

  apollo: {
    title: {
      query: PAGE_TITLE_DETAILS_QUERY,
      variables() {
        return {page: this.titleVars}
      },
      result(res) {
        if(this.$route && !res.stale && this.loaded != this.$route.params.page_id) {
          this.loaded = this.$route.params.page_id
          if(res.data.title.score != this.page.title_score) {
            _zyppy.track('Score Mismatched', {
              page_id: this.$route.params.page_id,
              title: res.data.title.text,
              page_title: this.page.custom_title || this.page.title,
              title_score: res.data.title.score,
              page_score: this.page.title_score,
              title_reasons: res.data.title.reasons.filter(e => e.values.length).map(e => e.id),
              missing_words: res.data.title.reasons.find(e => e.id == 'missing').values,
              updated_at: this.page.updated_at
            })
          }
        }
      },
      skip() {
        if(this.isDemo) return false
        return !this.page.id || !this.site.total_pages
      },
    },
    fragment: {
      query: SITE_FRAGMENTS_QUERY,
      variables() {
        return {
          site_id: this.site.id,
          count: MIN_COUNT
        }
      },
      skip() {
        return !this.site.id
      },
      fetchPolicy: 'cache-first',
    },
  },
  computed: {
    titleVars() {
      // @todo title update is causing mutation and query to fire
      const page = _.pick(this.page, ['canonical', 'id', 'title', 'custom_title', 'title_score', 'url', 'site', 'status_code', 'is_rewritten', 'serp_title', 'redirects_to', 'h1'])
      page.queries = this.page.queries.map(e => _.omit(e, '__typename'))
      page.page_issues = this.page.page_issues.map(e => ({
        id: e.id,
        name: e.name,
        ignored: e.ignored,
        data: (e.data || []).map(_.toString)
      }))
      page.ignored_words = this.page.query_words.filter(e => e.ignored).map(e => _.pick(e, ['word', 'stem']))
      page.site = {
        id: this.page.site_id,
        fragments: this.fragment.map(e => _.omit(e, '__typename')),
        total_pages: _.get(this, 'site.total_pages.aggregate.count'),
      }
      if(this.isDemo) {
        page.custom_title = this.custom_title || this.page.title
      }
      if(this.page.redirect) {
        page.redirect = _.omit(this.page.redirect, '__typename')
      }
      return page
    },
    hasPublishing() {
      return !!_.get(this.site, 'settings.cloudflare.ENABLED') || !!_.get(this.site, 'settings.wordpress.ENABLED')
    },
    actions() {
      const $vm = this
      const actions = []
      if(!this.editable && !this.hasPublishing && !this.isDemo && !this.isDuda) {
        return actions
      }
      if(this.editable) {
        if((!this.isDemo && !this.isDuda) && this.page.is_draft) {
          actions.push({label: 'Save', onClick: () => $vm.$emit('approve') && $vm.cancel()})
        } 
        if(this.title.text != this.page.title && navigator.clipboard.writeText) {
          const label = this.clipboardText == $vm.title.text ? 'Copied' : 'Copy'
          actions.push({label, onClick: (e) => {
            $vm.writeToClipboard($vm.title.text)
            $vm.cancel(e)
          }})
        } else {
          actions.push({label: 'Cancel', onClick: (e) => $vm.cancel(e)})
        }
      } else if(!this.isDuda) {
        actions.push({label: 'Edit', onClick: (e) => $vm.edit(e)})
      }
      if(this.page.approved_at || this.page.is_draft) {
        actions.push({
          label: 'Revert',
          tooltip: this.page.title,
          onClick: () => $vm.$emit('revert'),
        })
      }
      return actions
    }
  },
  mounted() {
    if(this.page.title) {
      this.setText(this.page.custom_title || this.page.title)
      this.focus()
      const $vm = this
      // Prevent rich text from being pasted
      if(window.ClipboardEvent) {
        this.$refs.title_edit.addEventListener('paste', function(e) {
          e.preventDefault()
          const text = (e.clipboardData || window.clipboardData).getData('text')
          try {
            document.execCommand('insertHTML', false, text)
          } catch(e) {
            $vm.$refs.title_edit.innerText += text
          }
        })
        // document.addEventListener('copy', e => {
        //   $vm.clipboardText = (e.clipboardData || window.clipboardData).getData('text')
        // })
      }
    }
  },
  watch: {
    page: {
      deep: true,
      handler: async function(to, from) {
        // Handle page changes, and re-crawls
        if(!from || from.id != to.id || from.title != to.title) {
          this.setText(to.custom_title || to.title)
        }
        if(to.is_draft && !from.is_draft) {
          this.edit()
        }
        this.loading = false
        await this.$nextTick()
        this.focus()
      }
    },
    title: {
      deep: true,
      immediate: true,
      handler() {
        this.$emit('update', this.title)
      }
    },
    'page.serp_title': {
      immediate: true,
      handler() {
        this.getSerpScore()
      }
    }
  },
  methods: {
    async edit($event) {
      this.editable = true
      await this.$nextTick()
      this.focus()
    },
    onBlur($event) {
      if($event.relatedTarget && $event.relatedTarget.classList.contains('modal-content')) {
        this.cancel()
      }
    },
    async cancel($event) {
      if(this.editable) {
        this.editable = false
      }
    },
    focus() {
      if(document.activeElement != this.$refs.title_edit) {
        this.$refs.title_edit.focus()
      }
    },
    // Update editor
    setText(text) {
      this.$refs.title_edit.innerText = text
    },
    removeFragment(str) {
      _zyppy.track('Fragment Removed')
      const title = this.setText(title.text.replace(str, ''))
      this.save(title)
    },
    async save(val) {
      // Update DB and editor
      this.setText(val)
      this.update(val)
    },
    debounce_update: _.debounce(function(val) {
      return this.update(val)
    }, 200),
    async update(val) {
      // Update DB but not the editor
      const custom_title = (val || '').replace(/\n/, '').trim()
      if(!custom_title) {
        this.$refs.title_edit.innerText = this.page.title
      }
      if(this.edited != this.page.id) {
        this.edited = this.page.id
        _zyppy.track('Title Edited')
      }
      if(this.isDemo) {
        this.custom_title = custom_title
        return
      }
      await this.$apollo.mutate({
        mutation: PAGE_TITLE_DETAILS_MUTATION,
        variables: {
          page: {
            ...this.titleVars,
            custom_title,
          }
        },
        // Update cache since this wasn't done via Hasura
        update: (store, { data: { title } }) => {
          const options = {
            query: PAGE_QUERY,
            variables: {id: this.page.id}
          }
          const data = store.readQuery(options)
          data.page.custom_title = title.text != data.page.title ? title.text : null
          data.page.title_score = title.score
          if(!this.page.status && this.page.status_inferred == 'not_started') {
            data.page.status_inferred = 'in_progress'
          }
          data.page.is_draft = (data.page.optimized_title && title.text != data.page.optimized_title)
            || (!data.page.optimized_title && title.text != data.page.title)
          // @TODO causes jank :(
          store.writeQuery({
            ...options,
            data
          })
        }
      })
    },
    async getSerpScore() {
      if(this.page.is_rewritten) {
        const response = await this.$apollo.query({
          query: PAGE_TITLE_DETAILS_QUERY,
          variables: {
            page: {...this.titleVars, title: this.page.serp_title}
          }
        })
        this.serp_title = response.data.title
        this.$emit('serp_score', this.serp_title.score)
      }
    },
    async writeToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
      } catch(e) {
        console.error(e)
      }
    }
  }
}
</script>